/**
 * Pure block model for the drag-and-drop visual HTML builder.
 *
 * The visual builder lets users compose an invoice/contract template by
 * dragging pre-built blocks (heading, paragraph, image, table, divider,
 * variable) onto a canvas and reordering them. This module owns the block
 * data model and the serialization of blocks into the HTML string that the
 * backend render service expands (Handlebars tokens such as `{{Factura.Nr}}`
 * pass through untouched).
 */

export type BuilderBlockType =
    | 'heading'
    | 'paragraph'
    | 'image'
    | 'table'
    | 'divider'
    | 'verticalLine'
    | 'variable'
    | 'html';

export interface BuilderBlock {
    id: string;
    type: BuilderBlockType;
    content: string;
}

export const BUILDER_BLOCK_TYPES: ReadonlyArray<BuilderBlockType> = [
    'heading',
    'paragraph',
    'image',
    'table',
    'divider',
    'verticalLine',
    'variable',
    'html',
];

export const DEFAULT_BLOCK_CONTENT: Record<BuilderBlockType, string> = {
    heading: 'Heading',
    paragraph: 'Paragraph text',
    image: '{{asset.logo}}',
    table: '{{ItemsTable}}',
    divider: '',
    verticalLine: '',
    variable: '{{Factura.Nr}}',
    html: '<div></div>',
};

let blockIdCounter = 0;

export const createBuilderBlockId = (): string => {
    blockIdCounter += 1;
    return `block-${blockIdCounter}`;
};

export const createBuilderBlock = (type: BuilderBlockType, content?: string): BuilderBlock => ({
    id: createBuilderBlockId(),
    type,
    content: content ?? DEFAULT_BLOCK_CONTENT[type],
});

const escapeHtml = (value: string): string =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const escapeAttribute = (value: string): string => escapeHtml(value).replace(/"/g, '&quot;');

const BLOCK_RENDERERS: Record<BuilderBlockType, (block: BuilderBlock) => string> = {
    heading: block => `<h2>${escapeHtml(block.content)}</h2>`,
    paragraph: block => `<p>${escapeHtml(block.content)}</p>`,
    image: block => `<img src="${escapeAttribute(block.content)}" alt="" />`,
    table: block => block.content,
    divider: () => '<hr />',
    verticalLine: () => '<div style="width: 0; height: 80px; border-left: 1px solid #222;"></div>',
    variable: block => `<p>${escapeHtml(block.content)}</p>`,
    html: block => block.content,
};

export const serializeBlockToHtml = (block: BuilderBlock): string =>
    BLOCK_RENDERERS[block.type](block);

export const serializeBlocksToHtml = (blocks: ReadonlyArray<BuilderBlock>): string =>
    blocks.map(serializeBlockToHtml).join('\n');

export const insertBuilderBlock = (
    blocks: ReadonlyArray<BuilderBlock>,
    block: BuilderBlock,
    index: number,
): BuilderBlock[] => {
    const next = [...blocks];
    const clamped = Math.max(0, Math.min(index, next.length));
    next.splice(clamped, 0, block);
    return next;
};

export const moveBuilderBlock = (
    blocks: ReadonlyArray<BuilderBlock>,
    fromIndex: number,
    toIndex: number,
): BuilderBlock[] => {
    if (fromIndex === toIndex) return [...blocks];
    if (fromIndex < 0 || fromIndex >= blocks.length) return [...blocks];
    if (toIndex < 0 || toIndex >= blocks.length) return [...blocks];
    const next = [...blocks];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
};

export const updateBuilderBlockContent = (
    blocks: ReadonlyArray<BuilderBlock>,
    id: string,
    content: string,
): BuilderBlock[] => blocks.map(block => (block.id === id ? { ...block, content } : block));

export const removeBuilderBlock = (
    blocks: ReadonlyArray<BuilderBlock>,
    id: string,
): BuilderBlock[] => blocks.filter(block => block.id !== id);

const NODE_TYPE_ELEMENT = 1;
const NODE_TYPE_TEXT = 3;
const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const SINGLE_VARIABLE_PATTERN = /^\{\{[^{}]+\}\}$/;
const TABLE_TOKEN_PATTERN = /^\{\{(ItemsTable|ObjectsTable)\}\}$/;

const tokenToBlock = (token: string): BuilderBlock => {
    if (TABLE_TOKEN_PATTERN.test(token)) return createBuilderBlock('table', token);
    return createBuilderBlock('variable', token);
};

export const readNodeText = (value: string | null): string => value ?? '';

const textToBlock = (text: string): BuilderBlock | null => {
    const trimmed = text.trim();
    if (!trimmed) return null;
    if (SINGLE_VARIABLE_PATTERN.test(trimmed)) return tokenToBlock(trimmed);
    return createBuilderBlock('paragraph', trimmed);
};

const elementToBlock = (element: Element): BuilderBlock => {
    const tag = element.tagName;
    if (HEADING_TAGS.has(tag))
        return createBuilderBlock('heading', readNodeText(element.textContent));
    if (tag === 'P') {
        const text = readNodeText(element.textContent).trim();
        if (SINGLE_VARIABLE_PATTERN.test(text)) return tokenToBlock(text);
        return createBuilderBlock('paragraph', text);
    }
    if (tag === 'IMG') return createBuilderBlock('image', element.getAttribute('src') ?? '');
    if (tag === 'HR') return createBuilderBlock('divider', '');
    return createBuilderBlock('html', element.outerHTML);
};

/**
 * Parse an existing template's HTML into editable builder blocks so a saved
 * template can be imported into the visual builder. Recognised top-level
 * elements (headings, paragraphs, images, dividers, single variable/table
 * tokens) map to typed blocks; anything else is preserved verbatim as a raw
 * `html` block so no markup is lost during import.
 */
export const parseHtmlToBlocks = (html: string): BuilderBlock[] => {
    if (!html.trim()) return [];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const blocks: BuilderBlock[] = [];
    doc.body.childNodes.forEach(node => {
        if (node.nodeType === NODE_TYPE_TEXT) {
            const block = textToBlock(readNodeText(node.textContent));
            if (block) blocks.push(block);
            return;
        }
        if (node.nodeType === NODE_TYPE_ELEMENT) {
            blocks.push(elementToBlock(node as Element));
        }
    });
    return blocks;
};
